import { useRef, useState } from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';
import { RevelioTooltip } from '@revelio/ui';

interface Props {
  prompt: string;
  response: TalentDiscoveryAiFilterSearchResponseV2;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
}

interface HighlightInfo {
  text: string;
  isMatched: boolean;
  isPartialMatch?: boolean;
  startIndex: number;
  endIndex: number;
  explanation?: string;
}

export const ResponseHighlightedOverlay = ({
  prompt,
  response,
  textAreaRef,
}: Props) => {
  const overlayRef = useRef<HTMLSpanElement>(null);
  const [hoveredPromptIndex, setHoveredPromptIndex] = useState<number | null>(
    null
  );

  // Get highlighted segments from the AI response
  const segments = getHighlightedSegments({ prompt, response });

  // Handle clicks on the entire overlay area
  const handleOverlayClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    // Stop event propagation to prevent the parent Box's click handler from firing
    e.stopPropagation();

    if (!textAreaRef.current || !overlayRef.current) return;

    // Find the element that was clicked
    const targetElement = document.elementFromPoint(e.clientX, e.clientY);
    if (!targetElement || !overlayRef.current.contains(targetElement)) return;

    // Get the position from the clicked element's data attribute
    const startIndex = targetElement.getAttribute('data-highlight-start-index');
    if (startIndex) {
      const position = parseInt(startIndex, 10);
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(position, position);
    } else {
      // Fallback to end of text
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(prompt.length, prompt.length);
    }
  };

  // Render highlighted text with proper colors and tooltips
  const renderHighlightedText = () => {
    // Sort segments by start index to process them in order
    const sortedSegments = [...segments].sort(
      (a, b) => a.startIndex - b.startIndex
    );

    // Remove overlapping segments - keep the longer one
    const nonOverlappingSegments: HighlightInfo[] = [];
    let lastEndIndex = 0;

    sortedSegments.forEach((segment) => {
      if (segment.startIndex >= lastEndIndex) {
        // No overlap, add this segment
        nonOverlappingSegments.push(segment);
        lastEndIndex = segment.endIndex;
      } else if (segment.endIndex > lastEndIndex) {
        // Overlap detected, but this segment extends further
        // Replace the last segment with this one if it's longer
        const lastSegment =
          nonOverlappingSegments[nonOverlappingSegments.length - 1];
        if (
          lastSegment &&
          segment.endIndex - segment.startIndex >
            lastSegment.endIndex - lastSegment.startIndex
        ) {
          nonOverlappingSegments[nonOverlappingSegments.length - 1] = segment;
          lastEndIndex = segment.endIndex;
        }
      }
      // If this segment is completely contained within the last segment, skip it
    });

    const result = [];
    let lastIndex = 0;

    nonOverlappingSegments.forEach((segment, idx) => {
      // Add any text before this segment
      if (segment.startIndex > lastIndex) {
        result.push(
          <span key={`plain-${lastIndex}`} style={{ cursor: 'text' }}>
            {prompt.substring(lastIndex, segment.startIndex)}
          </span>
        );
      }

      // Determine highlight type for test data attribute
      let highlightType = 'unmatched';
      if (segment.isMatched) {
        highlightType = 'matched';
      } else if (segment.isPartialMatch) {
        highlightType = 'partial-match';
      }

      const highlightText = prompt.substring(
        segment.startIndex,
        segment.endIndex
      );

      const style = {
        color: getTextColor(segment.isMatched, !!segment.isPartialMatch),
        borderBottom: getBorderBottom(
          segment.isMatched,
          !!segment.isPartialMatch
        ),
        cursor: 'text',
        fontWeight: '400',
      };

      const highlightElement = (
        <span
          key={`highlight-${idx}`}
          data-testid={`ai-highlight-${highlightType}`}
          data-highlight-text={highlightText}
          data-highlight-start-index={segment.startIndex}
          style={style}
          onMouseOver={() => setHoveredPromptIndex(segment.startIndex)}
          onMouseLeave={() => setHoveredPromptIndex(null)}
        >
          {highlightText}
        </span>
      );

      // Wrap in tooltip if there's an explanation
      if (segment.explanation) {
        result.push(
          <RevelioTooltip
            key={`tooltip-${idx}`}
            label={segment.explanation}
            isDisabled={!segment.explanation}
            isOpen={hoveredPromptIndex === segment.startIndex}
          >
            {highlightElement}
          </RevelioTooltip>
        );
      } else {
        result.push(highlightElement);
      }

      lastIndex = segment.endIndex;
    });

    // Add any remaining text
    if (lastIndex < prompt.length) {
      result.push(
        <span key={`plain-${lastIndex}`} style={{ cursor: 'text' }}>
          {prompt.substring(lastIndex)}
        </span>
      );
    }

    return result;
  };

  return (
    <span
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{ userSelect: 'none' }}
      data-testid="ai-highlight-overlay"
    >
      {renderHighlightedText()}
    </span>
  );
};

const getHighlightedSegments = ({
  prompt,
  response,
}: {
  prompt: string;
  response: TalentDiscoveryAiFilterSearchResponseV2;
}): HighlightInfo[] => {
  const segments: HighlightInfo[] = [];

  const processMatchedText = (relevantPromptText: string) => {
    const text = String(relevantPromptText ?? '').trim();
    if (!text) return;

    const index = prompt.toLowerCase().indexOf(text.toLowerCase());

    if (index !== -1) {
      // Check for duplicates using case-insensitive comparison
      const isDuplicate = segments.some(
        (segment) =>
          segment.text.toLowerCase() === text.toLowerCase() &&
          segment.startIndex === index &&
          segment.endIndex === index + text.length
      );

      if (!isDuplicate) {
        segments.push({
          text,
          isMatched: true,
          startIndex: index,
          endIndex: index + text.length,
        });
      }
    }
  };

  const valid = response.relevantPromptText.valid ?? [];
  const invalid = response.relevantPromptText.invalid ?? [];

  const explanationByPromptText = new Map<string, string>();
  response.unknownFilters.forEach((u) => {
    const key = (u.relevantPromptText || '').toLowerCase().trim();
    if (!key) return;
    const explanation = u.explanation;
    if (typeof explanation === 'string' && explanation.trim()) {
      explanationByPromptText.set(key, explanation);
    }
  });

  valid.forEach(processMatchedText);

  invalid.forEach((t) => {
    const text = String(t ?? '').trim();
    if (!text) return;

    const index = prompt.toLowerCase().indexOf(text.toLowerCase());
    if (index === -1) return;

    const key = text.toLowerCase();
    const explanation = explanationByPromptText.get(key);

    segments.push({
      text,
      isMatched: false,
      startIndex: index,
      endIndex: index + text.length,
      explanation,
    });
  });

  return segments;
};

// Helper functions to avoid nested ternaries
const getBorderBottom = (
  isMatched: boolean,
  isPartialMatch: boolean
): string | undefined => {
  if (isMatched) return undefined; // No underline for matched filters
  if (isPartialMatch) return '1px solid #FF9633'; // Orange for partial match
  return '1px solid #EE002B'; // Red for unmatched
};

const getTextColor = (isMatched: boolean, isPartialMatch: boolean): string => {
  if (isMatched) return '#16BD5E'; // Green for matched
  if (isPartialMatch) return '#FF9633'; // Orange for partial match
  return '#EE002B'; // Red for unmatched
};
